/**
 * Live data for the design sheet's chart previews (TemplateChartPreview).
 *
 * Univer hands a float DOM its `data` once, when it is added. So the layer carries only the
 * chart's id, and the designer PUBLISHES the plotted values here every time a cell changes —
 * the chart follows the sheet (owner request 2026-10-12: "if I change value chart should react").
 */

export interface ChartPreviewData {
  points: { x: number; y: number | null }[];
  yTitle: string | null;
  legend: boolean;
  markers: boolean;
  /** number format of the plotted Y cells (Excel links the axis labels to it) */
  yFormat?: string | null;
}

const charts = new Map<string, ChartPreviewData>();
const listeners = new Map<string, Set<() => void>>();

/** Set (or replace) a chart's data and redraw it. */
export function publishChart(id: string, data: ChartPreviewData): void {
  charts.set(id, data);
  listeners.get(id)?.forEach(fn => fn());
}

const cleanups = new Map<string, (() => void)[]>();

/** Forget charts whose id starts with `prefix` (a sheet's own, when it closes) and stop the
 *  timers / listeners registered for that prefix. */
export function dropCharts(prefix: string): void {
  for (const id of [...charts.keys()]) if (id.startsWith(prefix)) charts.delete(id);
  for (const fn of cleanups.get(prefix) ?? []) { try { fn(); } catch { /* already gone */ } }
  cleanups.delete(prefix);
}

/** Run `fn` when dropCharts(prefix) is called. */
export function onDropCharts(prefix: string, fn: () => void): void {
  cleanups.set(prefix, [...(cleanups.get(prefix) ?? []), fn]);
}

/** The chart's current data (useSyncExternalStore snapshot). */
export function chartData(id: string): ChartPreviewData | undefined {
  return charts.get(id);
}

/** Redraw subscription (useSyncExternalStore subscribe). */
export function subscribeChart(id: string, fn: () => void): () => void {
  const set = listeners.get(id) ?? new Set();
  set.add(fn);
  listeners.set(id, set);
  return () => { set.delete(fn); if (!set.size) listeners.delete(id); };
}
